import { ReactNode } from "react";

import { ArrowLeft, Loader2, AlertCircle, RefreshCw } from "lucide-react";

import { Button } from "@/modern/components/ui/button-shadcn";

import { ExtractionType, EXTRACTION_OPERATIONS } from "./types";

interface ExtractionResultsViewProps {
  type: ExtractionType;
  loading: boolean;
  error: string | null;
  onBack: () => void;
  onRetry: () => void;
  onReExtract?: () => void;
  resultCount?: number;
  loadingPhase?: "analyzing" | "extracting" | null;
  children: ReactNode;
}

const LOADING_MESSAGES: Record<ExtractionType, string> = {
  dates: "Extracting dates from document...",
  summary: "Generating document summary...",
  checklist: "Extracting checklists from document...",
  location: "Finding locations in document...",
  budget: "Extracting budget information...",
  client: "Finding clients and organizations...",
  notes: "Extracting notes from document...",
};

const EMPTY_MESSAGES: Record<ExtractionType, string> = {
  dates: "No dates found in this document.",
  summary: "Could not generate a summary for this document.",
  checklist: "No checklists found in this document.",
  location: "No locations found in this document.",
  budget: "No budget information found in this document.",
  client: "No clients or organizations found in this document.",
  notes: "No notes found in this document.",
};

const ANALYZING_MESSAGE = "Analyzing document...";

export const ExtractionResultsView = ({ type, loading, error, onBack, onRetry, onReExtract, resultCount, loadingPhase, children }: ExtractionResultsViewProps) => {
  const operation = EXTRACTION_OPERATIONS.find((op) => op.type === type);
  const label = operation?.label || "Extraction";

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border shrink-0">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onBack}
          aria-label="Back to operations"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <span className="text-sm font-medium text-foreground truncate flex-1">
          {label}
          {resultCount !== undefined && resultCount > 0 && (
            <span className="text-muted-foreground font-normal"> ({resultCount})</span>
          )}
        </span>
        {onReExtract && !loading && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onReExtract}
            aria-label="Re-extract"
            title="Re-extract from document"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading && (
          <div className="flex flex-col items-center justify-center gap-3 py-12 px-4">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
            <p className="text-sm text-muted-foreground text-center">
              {loadingPhase === "analyzing" ? ANALYZING_MESSAGE : LOADING_MESSAGES[type]}
            </p>
          </div>
        )}

        {error && !loading && (
          <div className="flex flex-col items-center justify-center gap-3 py-12 px-4">
            <AlertCircle className="w-6 h-6 text-destructive" />
            <p className="text-sm text-destructive text-center">{error}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRetry}
              className="gap-1.5"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Retry
            </Button>
          </div>
        )}

        {!loading && !error && resultCount === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 py-12 px-4">
            <p className="text-sm text-muted-foreground text-center">
              {EMPTY_MESSAGES[type]}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRetry}
              className="gap-1.5"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Try Again
            </Button>
          </div>
        )}

        {!loading && !error && (resultCount === undefined || resultCount > 0) && children}
      </div>
    </div>
  );
};
